import { useMemo } from "react";
import { MenuPlacement } from "react-select";
import cn from "classnames";
import { Nullable } from "shared/lib/typeCheck.lib";
import { useTranslation } from "shared/localization/localization";
import { ComponentsClassNames, CustomSelect } from "shared/ui/CustomSelect";
import { AllParcelCarriersIcon } from "shared/ui/Icons";
import { createParcelCarrierIconUrl } from "shared/utils/url.utils";
import styles from "./ParcelCarriersSelect.module.scss";

import { useParcelCarriersQuery } from "../api/hooks/useParcelCarriersQuery";

type CarrierOptionType = {
  value: string;
  label: string;
  icon: JSX.Element;
  isDropDownMenuRelative?: boolean;
};

type ParcelCarriersSelectProps = {
  value: Nullable<string>;
  onChange: (value: CarrierOptionType) => void;
  className?: string;
  isDropDownMenuRelative?: boolean;
  menuPlacement?: MenuPlacement;
  componentsClassNames?: ComponentsClassNames;
  showAllCarriersOption?: boolean;
  placeholderDataTestId?: string;
  isDisabled?: boolean;
};

export const ParcelCarriersSelect: RFC<ParcelCarriersSelectProps> = ({
  value,
  onChange,
  className,
  isDropDownMenuRelative = true,
  componentsClassNames,
  menuPlacement = "auto",
  showAllCarriersOption = false,
  placeholderDataTestId,
  isDisabled = false,
}) => {
  const { t } = useTranslation();
  const { parcelCompanyOptions } = useParcelCarriersQuery();

  const carrierOptions: CarrierOptionType[] = useMemo(() => {
    const defaultOption: CarrierOptionType = {
      value: "",
      label: t("All carriers"),
      icon: <AllParcelCarriersIcon />,
    };
    return [
      ...(showAllCarriersOption ? [defaultOption] : []),
      ...parcelCompanyOptions.map((parcelCompany) => ({
        value: parcelCompany.scacCode,
        label: parcelCompany.name,
        icon: (
          <img
            src={createParcelCarrierIconUrl(parcelCompany.scacCode)}
            alt={parcelCompany.name}
            width={20}
            height={20}
            className={styles.scacImg}
            onError={(e) => {
              const img = e.currentTarget;
              if (!img.classList.contains(styles.hidden)) {
                img.classList.add(styles.hidden);
              }
            }}
          />
        ),
      })),
    ];
  }, [parcelCompanyOptions.length]);

  const carrierValue = useMemo(() => {
    return carrierOptions.find((option) => option.value === value) ?? null;
  }, [value, carrierOptions]);

  return (
    <CustomSelect<CarrierOptionType, false>
      className={className}
      placeholder={
        <div className={styles.placeholder}>
          <AllParcelCarriersIcon />
          {t("All carriers")}
        </div>
      }
      placeholderDataTestId={placeholderDataTestId}
      isDisabled={isDisabled}
      options={carrierOptions}
      value={carrierValue}
      handleChange={onChange}
      isInputTypingEnabled
      menuPlacement={menuPlacement}
      isDropDownMenuRelative={isDropDownMenuRelative}
      selectedIndicator="checkmark"
      lazyLoadedMenuList
      componentsClassNames={{
        menuList: cn(styles.override, styles.shipmentSelectMenuList),
        ...componentsClassNames,
      }}
    />
  );
};
